import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Sparkles, Gift, PlusCircle, Search } from "lucide-react";

const PAID_TIERS = [
  { value: "petite", label: "Petite ($35 / 30d)" },
  { value: "classic", label: "Classic ($55 / 60d)" },
  { value: "grand", label: "Grand ($120 / 365d)" },
];

// Default expiry helper — today + 60 days as yyyy-mm-dd
const defaultExpiry = () => {
  const d = new Date();
  d.setDate(d.getDate() + 60);
  return d.toISOString().slice(0, 10);
};

const isoFromDate = (dateStr) => {
  if (!dateStr) return null;
  // Treat the date as end-of-day UTC so an admin picking "today" still lands in the future.
  return new Date(`${dateStr}T23:59:59.000Z`).toISOString();
};

const CompExistingForm = ({ onSuccess }) => {
  const [eventId, setEventId] = useState("");
  const [tier, setTier] = useState("classic");
  const [expiresAt, setExpiresAt] = useState(defaultExpiry());
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  // Host-lookup helper — paste an email, we surface all their events so the
  // admin can copy the correct UUID with one click.
  const [lookupEmail, setLookupEmail] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupErr, setLookupErr] = useState("");
  // Bulk-comp state — a set of event_ids selected via checkbox in the lookup list.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllShown = () => {
    if (!lookupResult?.events) return;
    setSelectedIds(new Set(lookupResult.events.map((e) => e.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkComp = async () => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true); setMsg(null);
    try {
      const res = await api.post("/admin/sponsor/bulk-comp", {
        event_ids: Array.from(selectedIds),
        tier,
        expires_at: isoFromDate(expiresAt),
        reason: reason || null,
      });
      const { succeeded, failed } = res.data;
      setMsg({
        ok: failed === 0,
        text: failed === 0
          ? `Comped ${succeeded} event${succeeded === 1 ? "" : "s"} → ${tier} until ${expiresAt}.`
          : `Comped ${succeeded} of ${succeeded + failed}. ${failed} failed — check the log.`,
      });
      clearSelection();
      onSuccess?.();
      // Refresh lookup so tier/expiry badges reflect the change.
      if (lookupEmail) runLookup();
    } catch (err) {
      setMsg({ ok: false, text: err?.response?.data?.detail || "Bulk comp failed." });
    } finally { setBulkBusy(false); }
  };

  const runLookup = async () => {
    const q = (lookupEmail || "").trim().toLowerCase();
    if (!q) return;
    setLookupBusy(true); setLookupErr(""); setLookupResult(null);
    try {
      const r = await api.get(`/admin/host-lookup?email=${encodeURIComponent(q)}`);
      setLookupResult(r.data);
      if (!r.data.user) setLookupErr("No host found with this email.");
    } catch (err) {
      setLookupErr(err?.response?.data?.detail || "Lookup failed.");
    } finally { setLookupBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const res = await api.post("/admin/sponsor/comp", {
        event_id: eventId.trim(),
        tier,
        expires_at: isoFromDate(expiresAt),
        reason: reason || null,
      });
      setMsg({ ok: true, text: `Comped "${res.data.name}" -> ${res.data.tier} until ${expiresAt}` });
      setEventId("");
      onSuccess?.();
    } catch (err) {
      setMsg({ ok: false, text: err?.response?.data?.detail || "Failed to comp event" });
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="sponsor-comp-form">
      {/* Host lookup */}
      <div className="bg-cream-100 rounded-2xl p-4 space-y-3">
        <div>
          <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Find event by host email</label>
          <div className="mt-1.5 flex gap-2">
            <input
              type="email" value={lookupEmail} onChange={(e) => setLookupEmail(e.target.value)}
              placeholder="host@example.com"
              data-testid="sponsor-comp-lookup-email"
              className="flex-1 px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm bg-white"
            />
            <button type="button" onClick={runLookup} disabled={lookupBusy || !lookupEmail.trim()}
              data-testid="sponsor-comp-lookup-btn"
              className="inline-flex items-center gap-1.5 bg-navy-700 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-navy-800 disabled:opacity-60">
              <Search className="w-4 h-4" /> {lookupBusy ? "Looking up…" : "Lookup"}
            </button>
          </div>
        </div>
        {lookupErr && <div className="text-xs text-red-600" data-testid="sponsor-comp-lookup-err">{lookupErr}</div>}
        {lookupResult?.user && (
          <div data-testid="sponsor-comp-lookup-result">
            <div className="text-xs text-navy-700/70">
              <span className="font-semibold text-navy-700">{lookupResult.user.name || "Unnamed"}</span> · {lookupResult.user.email}
              <span className="ml-2 text-navy-700/50">({lookupResult.events.length} event{lookupResult.events.length === 1 ? "" : "s"})</span>
            </div>
            {lookupResult.events.length === 0 ? (
              <div className="mt-2 text-xs text-navy-700/50">This host has no events yet.</div>
            ) : (
              <>
                <div className="mt-2 flex items-center justify-between gap-2 text-[11px]">
                  <div className="text-navy-700/60">
                    {selectedIds.size > 0 ? <><span className="font-semibold text-navy-700">{selectedIds.size}</span> selected</> : "Select multiple to bulk-comp them below."}
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={selectAllShown}
                      data-testid="sponsor-comp-select-all"
                      className="text-navy-700/70 underline hover:text-coral-300">Select all</button>
                    <button type="button" onClick={clearSelection} disabled={selectedIds.size === 0}
                      data-testid="sponsor-comp-clear"
                      className="text-navy-700/70 underline hover:text-coral-300 disabled:opacity-40 disabled:no-underline">Clear</button>
                  </div>
                </div>
                <ul className="mt-2 space-y-1.5 max-h-64 overflow-auto">
                  {lookupResult.events.map((ev) => (
                    <li key={ev.id} className="bg-white rounded-lg p-2.5 flex items-center gap-2">
                      <input type="checkbox" checked={selectedIds.has(ev.id)}
                        onChange={() => toggleSelected(ev.id)}
                        data-testid={`sponsor-comp-check-${ev.id}`}
                        className="w-4 h-4 accent-coral-300 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-navy-700 truncate">{ev.name}</div>
                        <div className="text-[11px] text-navy-700/50 truncate">
                          {ev.tier} · {ev.paid ? "paid" : "unpaid"}{ev.sponsored ? " · sponsored" : ""} · {ev.upload_count} uploads
                          {ev.expires_at ? ` · expires ${String(ev.expires_at).slice(0, 10)}` : ""}
                        </div>
                        <div className="text-[10px] text-navy-700/40 font-mono truncate">{ev.id}</div>
                      </div>
                      <button type="button" onClick={() => setEventId(ev.id)}
                        data-testid={`sponsor-comp-select-${ev.id}`}
                        className="text-xs bg-coral-100 text-coral-500 px-3 py-1.5 rounded-full font-semibold hover:bg-coral-200 whitespace-nowrap">
                        Use this
                      </button>
                    </li>
                  ))}
                </ul>
                {selectedIds.size > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 bg-coral-50 border border-coral-200 rounded-lg p-3">
                    <span className="text-xs text-navy-700/70">
                      Reactivate {selectedIds.size} event{selectedIds.size === 1 ? "" : "s"} → <b>{tier}</b> until <b>{expiresAt}</b>
                    </span>
                    <button type="button" onClick={bulkComp} disabled={bulkBusy}
                      data-testid="sponsor-comp-bulk-submit"
                      className="ml-auto inline-flex items-center gap-2 bg-coral-300 text-white px-4 py-2 rounded-full text-xs font-semibold hover:bg-coral-400 disabled:opacity-60">
                      <Gift className="w-3.5 h-3.5" /> {bulkBusy ? `Comping ${selectedIds.size}…` : `Comp ${selectedIds.size} events`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div>
        <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Event ID</label>
        <input
          value={eventId} onChange={(e) => setEventId(e.target.value)} required
          placeholder="paste the event UUID"
          data-testid="sponsor-comp-event-id"
          className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Tier</label>
          <select
            value={tier} onChange={(e) => setTier(e.target.value)}
            data-testid="sponsor-comp-tier"
            className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm bg-white"
          >
            {PAID_TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Expires at</label>
          <input
            type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} required
            data-testid="sponsor-comp-expires-at"
            className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm"
          />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Reason (optional — shown in sponsor log)</label>
        <input
          value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Renewal after expiry"
          data-testid="sponsor-comp-reason"
          maxLength={200}
          className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm"
        />
      </div>
      {msg && (
        <div data-testid="sponsor-comp-msg" className={`text-sm rounded-md px-3 py-2 ${msg.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>{msg.text}</div>
      )}
      <button disabled={busy} type="submit" data-testid="sponsor-comp-submit" className="inline-flex items-center gap-2 bg-coral-300 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-coral-400 disabled:opacity-60">
        <Gift className="w-4 h-4" /> {busy ? "Comping…" : "Comp event"}
      </button>
    </form>
  );
};

const CreateSponsoredForm = ({ onSuccess }) => {
  const [form, setForm] = useState({
    host_email: "", name: "", type: "wedding", date: "",
    description: "", tier: "classic", expires_at: defaultExpiry(),
  });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const payload = {
        ...form,
        host_email: form.host_email.trim().toLowerCase(),
        name: form.name.trim(),
        description: form.description.trim() || null,
        date: form.date || null,
        expires_at: isoFromDate(form.expires_at),
      };
      const res = await api.post("/admin/sponsor/create", payload);
      setMsg({ ok: true, text: `Sponsored event "${res.data.name}" (${res.data.tier}) created for ${form.host_email}. If they aren't registered yet, an invite email was sent so they can claim it.` });
      setForm({ ...form, name: "", host_email: "", date: "", description: "" });
      onSuccess?.();
    } catch (err) {
      setMsg({ ok: false, text: err?.response?.data?.detail || "Failed to create sponsored event" });
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="sponsor-create-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Host email</label>
          <input
            value={form.host_email} onChange={(e) => set("host_email", e.target.value)}
            required type="email" placeholder="host@example.com"
            data-testid="sponsor-create-host-email"
            className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Event name</label>
          <input
            value={form.name} onChange={(e) => set("name", e.target.value)}
            required placeholder="Sarah & Michael's Wedding"
            data-testid="sponsor-create-name"
            className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm"
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Type</label>
          <select
            value={form.type} onChange={(e) => set("type", e.target.value)}
            data-testid="sponsor-create-type"
            className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm bg-white"
          >
            {["wedding", "birthday", "corporate", "party", "other"].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Event date</label>
          <input
            type="date" value={form.date} onChange={(e) => set("date", e.target.value)}
            data-testid="sponsor-create-date"
            className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Tier</label>
          <select
            value={form.tier} onChange={(e) => set("tier", e.target.value)}
            data-testid="sponsor-create-tier"
            className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm bg-white"
          >
            {PAID_TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-navy-700 uppercase tracking-wider">Expires at</label>
        <input
          type="date" value={form.expires_at} onChange={(e) => set("expires_at", e.target.value)} required
          data-testid="sponsor-create-expires-at"
          className="mt-1.5 w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:outline-none focus:border-coral-300 text-sm"
        />
      </div>
      {msg && (
        <div data-testid="sponsor-create-msg" className={`text-sm rounded-md px-3 py-2 ${msg.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>{msg.text}</div>
      )}
      <button disabled={busy} type="submit" data-testid="sponsor-create-submit" className="inline-flex items-center gap-2 bg-navy-700 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-navy-800 disabled:opacity-60">
        <PlusCircle className="w-4 h-4" /> {busy ? "Creating…" : "Create sponsored event"}
      </button>
    </form>
  );
};

const SponsoredEventsPanel = () => {
  const [tab, setTab] = useState("comp");
  const [list, setList] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/sponsor/events?limit=25").then(r => setList(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-sponsor-card" className="bg-white rounded-2xl soft-shadow p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-coral-400" />
        <h2 className="font-semibold text-navy-700 text-lg">Sponsored events</h2>
      </div>
      <p className="text-sm text-navy-700/60 -mt-2">
        Grant a paid tier for free (comp an existing event) or create a fully-provisioned sponsored event under any host&apos;s account. If the host isn&apos;t registered yet, they&apos;ll receive an invite email to claim it.
      </p>

      <div className="flex gap-2 border-b border-cream-200">
        <button
          onClick={() => setTab("comp")}
          data-testid="sponsor-tab-comp"
          className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "comp" ? "border-coral-400 text-coral-500" : "border-transparent text-navy-700/60 hover:text-navy-700"}`}
        >Comp existing event</button>
        <button
          onClick={() => setTab("create")}
          data-testid="sponsor-tab-create"
          className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "create" ? "border-coral-400 text-coral-500" : "border-transparent text-navy-700/60 hover:text-navy-700"}`}
        >Create for a host</button>
      </div>

      {tab === "comp" ? <CompExistingForm onSuccess={load} /> : <CreateSponsoredForm onSuccess={load} />}

      <div data-testid="admin-sponsored-list" className="pt-4 border-t border-cream-200">
        <h3 className="text-xs uppercase tracking-wider font-semibold text-navy-700/60 mb-2">Recent sponsored events ({list.length})</h3>
        {list.length === 0 ? (
          <p className="text-sm text-navy-700/50">None yet.</p>
        ) : (
          <ul className="divide-y divide-cream-200">
            {list.map(item => (
              <li key={item.id} className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                <div className="min-w-0">
                  <div className="font-medium text-navy-700 truncate">{item.name}</div>
                  <div className="text-navy-700/50 text-xs truncate">
                    host: {item.host_email || "—"} · expires {item.expires_at ? new Date(item.expires_at).toLocaleDateString() : "—"}
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-wider rounded px-2 py-1 bg-coral-100 text-coral-500 font-bold">{item.tier}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SponsoredEventsPanel;
